import { useParams, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { SeoHead } from "@/components/SeoHead";
import { JsonLd, articleSchema, breadcrumbSchema } from "@/components/JsonLd";
import { articles } from "./Resources";
import { Button } from "@/components/ui/button";
import { Phone, ArrowLeft } from "lucide-react";
import { PHONE_HREF, PHONE_NUMBER } from "@/components/EmergencyBanner";

const ResourceArticle = () => {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <SeoHead
          title="Article Not Found (404) | Semper Fi Pump Service"
          description="The article you're looking for could not be found. Browse our full library of well pump resources."
          statusCode={404}
        />
        <div className="container py-16 text-center">
          <h1 className="text-2xl font-black mb-4 border-l-0 pl-0">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">This resource may have been moved or removed.</p>
          <Link to="/resources" className="text-accent hover:underline">Back to Resources</Link>
        </div>
      </Layout>
    );
  }

  const path = `/resources/${article.slug}`;
  return (
    <Layout>
      <SeoHead
        title={`${article.title} | Semper Fi Pump Service`}
        description={article.excerpt}
      />
      <JsonLd
        data={[
          articleSchema({ title: article.title, description: article.excerpt, path }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Resources", path: "/resources" },
            { name: article.title, path },
          ]),
        ]}
      />
      <section className="bg-primary">
        <div className="container py-16 md:py-24">
          <Link to="/resources" className="text-primary-foreground/60 text-sm inline-flex items-center gap-1 mb-4 hover:text-primary-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to Resources
          </Link>
          <h1 className="text-3xl md:text-4xl font-black text-primary-foreground border-l-0 pl-0">{article.title}</h1>
        </div>
      </section>

      <section className="container py-16 md:py-24 max-w-3xl">
        <p className="text-lg text-muted-foreground text-pretty mb-10 leading-relaxed">{article.excerpt}</p>
        {article.content.map((section, i) => (
          <div key={i} className="mb-10">
            <h2 className="text-xl font-bold mb-3">{section.heading}</h2>
            <p className="text-muted-foreground text-pretty leading-relaxed">{section.text}</p>
          </div>
        ))}

        <div className="bg-primary rounded-sm p-8 text-center mt-12">
          <h3 className="text-xl font-bold text-primary-foreground mb-4 border-l-0 pl-0">Need Help With This Issue?</h3>
          <p className="text-primary-foreground/80 mb-6 text-sm text-pretty">Don't try to fix it yourself. Call a Pocono Mountains pump expert.</p>
          <Button asChild variant="emergency" size="xl">
            <a href={PHONE_HREF}><Phone className="w-5 h-5" />Call {PHONE_NUMBER}</a>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ResourceArticle;
